/// <amd-module name="Mention/Common/DisplayNameStorageKeyTranslationService" />
import { isGuid } from "VSS/Core/Util/String";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { IdentityType, IIdentity } from "VSSUI/IdentityPicker";
import { getIgnoreMatch, parseCodeBlocksFromText, parseContainersFromText } from "./Util";

export interface IMentionTranslationService {
    translateDisplayNamesToStorageKeys(rawUntranslatedText: string): string;
    translateStorageKeysToDisplayNames(rawUntranslatedText: string): Promise<string>;
}

export interface IDisplayNameRecord {
    originalDisplayName: string;
    mangledDisplayName: string;
}

export class DisplayNameStorageKeyTranslationService extends VssService implements IMentionTranslationService {
    private displayNameToStorageKeyMap: { [key: string]: string } = {}; //Map to translate the displaynames in raw text to storage keys
    private storageKeyToDisplayNameMap: { [key: string]: IDisplayNameRecord } = {}; //ReverseMap used while rendering

    //Adds displayname and storage key to maps and returns the mangled (in case of conflicts)/original displayname that was stored finally
    //in the maps. NOTE: input parameter 'displayName' should always be an unmangled string - Ex: Foo Bar and NOT Foo Bar(1)
    public addDisplayNameAndStorageKey(displayName: string, storageKey: string): string {
        let retDisplayName: string = displayName;

        if (storageKey && displayName && isGuid(storageKey)) {
            if (!this.isDisplayNameAndStorageKeyAlreadyAdded(storageKey, displayName)) {
                //Make sure that the displayname is not mapped already to a different storage key and if so, mangle the display name to avoid
                //conflicts with existing keys
                retDisplayName = this.checkIfDisplayNameExistsInMapAndMangle(displayName);

                //It's ok to overwrite the existing keys as we want the user's latest display name associated with
                //the storage key
                this.displayNameToStorageKeyMap[retDisplayName] = storageKey.toUpperCase();
                this.storageKeyToDisplayNameMap[storageKey.toUpperCase()] = {
                    originalDisplayName: displayName,
                    mangledDisplayName: retDisplayName
                };
            }
        }

        return retDisplayName;
    }

    public getIdentityUsingStorageKeyAsync(storageKey: string): Promise<IIdentity | null> {
        if (!storageKey || !isGuid(storageKey)) {
            return Promise.resolve(null);
        } else {
            return this.getIdentityFromStorageKey(storageKey).then(
                (identity) => {
                    if (identity) {
                        this.addDisplayNameAndStorageKey(this.getIdentityId(identity), storageKey.toUpperCase());
                        return identity;
                    }
                    return null;
                },
                (error) => Promise.resolve(null)
            );
        }
    }

    private getIdentityFromStorageKey = (storageKey: string): Promise<IIdentity | undefined> => {
        const peopleProvider = new PeoplePickerProvider(this.pageContext);
        const identity = peopleProvider.getEntityFromUniqueAttribute(storageKey);
        const identityPromise = identity as Promise<IIdentity>;
        if ((identity as any).then) {
            return identityPromise;
        } else {
            return Promise.resolve(identity as IIdentity);
        }
    };

    private isHosted = () => {
        const pageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();
        return pageData && pageData.isHosted;
    };

    public getIdentityId(identity: IIdentity): string {
        if (this.isHosted()) {
            return identity.displayName || "";
        }
        if (identity.entityType === IdentityType.Group) {
            // Add '[' and ']' around the scope for groups.
            return `[${identity.scopeName}]\\${identity.samAccountName}`;
        }
        return `${identity.scopeName}\\${identity.samAccountName}`;
    }

    public getStorageKeyUsingDisplayName(displayName: string): string | null {
        if (displayName && this.displayNameToStorageKeyMap[displayName] && this.displayNameToStorageKeyMap.hasOwnProperty(displayName)) {
            return this.displayNameToStorageKeyMap[displayName];
        } else {
            return null;
        }
    }

    public translateDisplayNamesToStorageKeys(rawUntranslatedText: string): string {
        const codeBlocks = parseCodeBlocksFromText(rawUntranslatedText);
        const containerBlocks = parseContainersFromText(rawUntranslatedText);

        const regex = /\@\<(.*?)\>/g;
        const ignoreMatch = getIgnoreMatch(codeBlocks.concat(containerBlocks));
        let match;
        const matches = [];
        while ((match = regex.exec(rawUntranslatedText))) {
            matches.push(match);
        }
        for (let i = matches.length - 1; i >= 0; i--) {
            const currentMatch = matches[i];
            if (!ignoreMatch || !ignoreMatch(currentMatch.index)) {
                const translatedValue = this.getStorageKeyUsingDisplayName(currentMatch[1]);
                if (translatedValue) {
                    rawUntranslatedText =
                        rawUntranslatedText.substring(0, currentMatch.index) +
                        `@<${translatedValue}>` +
                        rawUntranslatedText.substring(currentMatch.index + currentMatch[0].length);
                }
            }
        }

        return rawUntranslatedText;
    }

    public translateStorageKeysToDisplayNames(rawUntranslatedText: string): Promise<string> {
        let currentPromise = Promise.resolve(rawUntranslatedText);
        const codeBlocks = parseCodeBlocksFromText(rawUntranslatedText);
        const containerBlocks = parseContainersFromText(rawUntranslatedText);
        const regex = /\@\<(.*?)\>/g;
        const ignoreMatch = getIgnoreMatch(codeBlocks.concat(containerBlocks));
        let match: RegExpExecArray | null;
        const matches = [];
        while ((match = regex.exec(rawUntranslatedText))) {
            matches.push(match);
        }
        for (let i = matches.length - 1; i >= 0; i--) {
            const currentMatch = matches[i];
            const storageKey = currentMatch[1];
            if ((!ignoreMatch || !ignoreMatch(currentMatch.index)) && isGuid(storageKey)) {
                const index = currentMatch.index;
                const length = currentMatch[0].length;
                if (this.storageKeyToDisplayNameMap[storageKey]) {
                    currentPromise = currentPromise.then((translatedText) => {
                        return (
                            translatedText.substring(0, index) +
                            `@<${this.storageKeyToDisplayNameMap[storageKey].mangledDisplayName}>` +
                            translatedText.substring(index + length)
                        );
                    });
                } else {
                    currentPromise = currentPromise.then((translatedText) => {
                        return this.getIdentityUsingStorageKeyAsync(storageKey).then((identity) => {
                            if (identity) {
                                return (
                                    translatedText.substring(0, index) +
                                    `@<${this.getIdentityId(identity)}>` +
                                    translatedText.substring(index + length)
                                );
                            }
                            return translatedText;
                        });
                    });
                }
            }
        }

        return currentPromise;
    }

    public isStorageKeyAlreadyAdded(storageKey: string): boolean {
        return !!(
            this.storageKeyToDisplayNameMap[storageKey.toUpperCase()] && this.storageKeyToDisplayNameMap.hasOwnProperty(storageKey.toUpperCase())
        );
    }

    private isDisplayNameAndStorageKeyAlreadyAdded(storageKey: string, displayName: string): boolean {
        return !!(
            this.isStorageKeyAlreadyAdded(storageKey) &&
            this.storageKeyToDisplayNameMap[storageKey.toUpperCase()].originalDisplayName === displayName &&
            this.displayNameToStorageKeyMap[displayName] &&
            this.displayNameToStorageKeyMap.hasOwnProperty(displayName) &&
            this.displayNameToStorageKeyMap[displayName] === storageKey.toUpperCase()
        );
    }

    /**
     * Checks if display name already exists in the translation map and returns a mangled version of it that is
     * not is not in the map yet
     * @param displayName
     */
    private checkIfDisplayNameExistsInMapAndMangle(displayName: string): string {
        let retDisplayName: string = displayName;

        if (this.displayNameToStorageKeyMap[displayName] && this.displayNameToStorageKeyMap.hasOwnProperty(displayName)) {
            let counter: number = 1;
            retDisplayName = displayName + "(" + counter + ")";
            while (this.displayNameToStorageKeyMap[retDisplayName]) {
                counter++;
                retDisplayName = displayName + "(" + counter + ")";
            }
        }

        return retDisplayName;
    }
}

export function translateDisplayNamesToStorageKeys(pageContext: IVssPageContext, rawUntranslatedText: string): string {
    const displayNameTranslationService = pageContext.getService<DisplayNameStorageKeyTranslationService>(
        "display-name-storage-key-translation-service"
    );
    return displayNameTranslationService.translateDisplayNamesToStorageKeys(rawUntranslatedText);
}

export function translateStorageKeysToDisplayNames(pageContext: IVssPageContext, rawUntranslatedText: string): Promise<string> {
    const displayNameTranslationService = pageContext.getService<DisplayNameStorageKeyTranslationService>(
        "display-name-storage-key-translation-service"
    );
    return displayNameTranslationService.translateStorageKeysToDisplayNames(rawUntranslatedText);
}

Services.add("display-name-storage-key-translation-service", { serviceFactory: DisplayNameStorageKeyTranslationService });
